import { useNavigate } from 'react-router-dom';
import Loader from '../../common/components/Loader';
import { useState } from 'react';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { ProfileService } from './Services/ProfileService';
import IconTextField from '../../common/components/IconTextfield';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faUser } from '@fortawesome/free-solid-svg-icons';
import {
    validatePassword,
    getPasswordRequirementsMessage,
} from '../../common/utils/passwordValidation';

export default function ResetPassword() {
    const navigate = useNavigate();
    const profileService = new ProfileService();
    const [alertType, setAlertType] = useState<'success' | 'error'>('error');
    const [isLoading, setIsLoading] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [showStatusMessage, setShowStatusMessage] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const handleSetNewPassword = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validatePassword(password)) {
            setStatusMessage(getPasswordRequirementsMessage());
            setShowStatusMessage(true);
            return;
        }

        if (password !== confirmPassword) {
            setStatusMessage('Passwords do not match.');
            setShowStatusMessage(true);
            return;
        }
        try {
            const params = new URLSearchParams(window.location.search);
            const token = decodeURIComponent(params.get('token'));
            const tenant = JSON.parse(localStorage.getItem('orgId'));
            setIsLoading(true);
            setShowStatusMessage(null);
            const result = await profileService.resetPassword(
                email,
                password,
                token,
                tenant
            );
            if (result.success) {
                setIsLoading(false);
                navigate('/login');
            } else {
                setIsLoading(false);
                setAlertType('error');
                setShowStatusMessage(result.error?.message);
            }
        } catch (e) {
            setIsLoading(false);
            setAlertType('error');
            if (e.response?.status === 401) {
                setStatusMessage(
                    'Fel i anropet. Har du skrivit rätt email och satt ett organisationsId?'
                );
            } else {
                setStatusMessage('Något gick fel!');
            }
        }
        setShowStatusMessage(true);
    };

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div>
            <Box display="flex" justifyContent="center" padding={4}>
                <Stack
                    spacing={2}
                    alignItems="center"
                    sx={{ maxWidth: '400px', width: '100%' }}
                >
                    <Paper
                        elevation={3}
                        sx={{ padding: 4, borderRadius: 2, width: '100%' }}
                    >
                        <Typography variant="h4" align="center" gutterBottom>
                            Sätt nytt lösenord
                        </Typography>
                        <Typography
                            variant="subtitle1"
                            align="center"
                            gutterBottom
                        >
                            Skriv in din email och ditt nya lösenord. Det måste
                            vara minst 6 tecken långt och innehålla minst en
                            stor bokstav och en siffra.
                        </Typography>

                        <Box
                            mt={2}
                            mb={2}
                            borderBottom={1}
                            borderColor="divider"
                        />

                        <Box
                            component="form"
                            onSubmit={handleSetNewPassword}
                            sx={{ mt: 1 }}
                        >
                            {showStatusMessage && (
                                <Alert severity={alertType} sx={{ mb: 2 }}>
                                    {statusMessage}
                                </Alert>
                            )}

                            <IconTextField
                                size="medium"
                                prefix={
                                    <FontAwesomeIcon
                                        icon={faUser}
                                        className="text-gray-400"
                                    />
                                }
                                label="Email"
                                placeholder="Email"
                                variant="outlined"
                                required
                                fullWidth
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                margin="normal"
                                autoComplete="off"
                            />

                            <IconTextField
                                size="medium"
                                prefix={
                                    <FontAwesomeIcon
                                        icon={faLock}
                                        className="text-gray-400"
                                    />
                                }
                                label="Nytt Lösenord"
                                placeholder="Lösenord"
                                variant="outlined"
                                required
                                fullWidth
                                type="password"
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                margin="normal"
                                autoComplete="off"
                            />

                            <IconTextField
                                size="medium"
                                prefix={
                                    <FontAwesomeIcon
                                        icon={faLock}
                                        className="text-gray-400"
                                    />
                                }
                                label="Bekräfta Lösenord"
                                placeholder="Bekräfta Lösenord"
                                variant="outlined"
                                required
                                fullWidth
                                type="password"
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={(e) =>
                                    setConfirmPassword(e.target.value)
                                }
                                margin="normal"
                                autoComplete="off"
                            />

                            <Stack
                                direction="row"
                                spacing={2}
                                justifyContent="center"
                                mt={3}
                            >
                                <Button
                                    type="button"
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => {
                                        navigate('/login');
                                    }}
                                    sx={{ minWidth: '120px' }}
                                >
                                    Avbryt
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    sx={{ mt: 3, mb: 2 }}
                                >
                                    Spara
                                </Button>
                            </Stack>
                        </Box>
                    </Paper>
                </Stack>
            </Box>
        </div>
    );
}
